import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {
  Box,
  ButtonBase,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import DateRangeSelector from "@entrilia/shared/components/DateRangeSelector.tsx";
import type { BalanceSheetPeriodPreset, BalanceSheetPeriodState } from "../utils/balanceSheetPeriod.ts";
import {
  applyBalanceSheetPresetSelection,
  createCustomBalanceSheetPeriodState,
  currentBalanceSheetPeriodMenuItems,
  customBalanceSheetPeriodMenuItem,
  getBalanceSheetPeriodDisplayLabel,
  getBalanceSheetPeriodMenuDescription,
  getBalanceSheetPeriodRange,
  isBalanceSheetPeriodShiftEnabled,
  lastBalanceSheetPeriodMenuItems,
  shiftBalanceSheetPeriod,
  todayBalanceSheetPeriodMenuItem,
} from "../utils/balanceSheetPeriod.ts";

interface Props {
  value: BalanceSheetPeriodState;
  onChange: (value: BalanceSheetPeriodState) => void;
  showTodayOption?: boolean;
}

const pickerHeight = 36;

const BalanceSheetPeriodPicker = ({ value, onChange, showTodayOption }: Props) => {
  const [triggerEl, setTriggerEl] = useState<HTMLElement | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [customRangeAnchorEl, setCustomRangeAnchorEl] = useState<HTMLElement | null>(null);
  const [showCustomRange, setShowCustomRange] = useState(false);

  const isShiftEnabled = isBalanceSheetPeriodShiftEnabled(value);
  const displayValue = getBalanceSheetPeriodDisplayLabel(value);
  const range = useMemo(() => getBalanceSheetPeriodRange(value), [value]);
  const isMenuOpen = !!menuAnchorEl;

  const handlePresetSelect = (preset: Exclude<BalanceSheetPeriodPreset, "CustomRange">) => {
    onChange(applyBalanceSheetPresetSelection(value, preset));
    setMenuAnchorEl(null);
  };

  return (
    <>
      <Stack
        direction="row"
        sx={[
          {
            alignItems: "stretch",
          },
          (theme) => ({
            height: pickerHeight,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: "hidden",
            "&:hover": {
              borderColor: theme.palette.secondary.main,
            },
          }),
        ]}
      >
        <IconButton
          size="small"
          sx={{ width: pickerHeight, borderRadius: 0 }}
          disabled={!isShiftEnabled}
          onClick={() => onChange(shiftBalanceSheetPeriod(value, "previous"))}
        >
          <ChevronLeftRoundedIcon fontSize="small" />
        </IconButton>

        <Divider orientation="vertical" flexItem />

        <ButtonBase
          ref={setTriggerEl}
          onClick={(event) => {
            setMenuAnchorEl(event.currentTarget);
            setCustomRangeAnchorEl(event.currentTarget);
          }}
          sx={{
            px: 1.25,
            minWidth: 198,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "text.primary",
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <CalendarTodayRoundedIcon fontSize="small" color="action" />
            <Typography variant="body1">{displayValue}</Typography>
          </Stack>
          <ArrowDropDownRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
        </ButtonBase>

        <Divider orientation="vertical" flexItem />

        <IconButton
          size="small"
          sx={{ width: pickerHeight, borderRadius: 0 }}
          disabled={!isShiftEnabled}
          onClick={() => onChange(shiftBalanceSheetPeriod(value, "next"))}
        >
          <ChevronRightRoundedIcon />
        </IconButton>
      </Stack>
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={() => setMenuAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 310,
            },
          },
        }}
      >
        {showTodayOption && (
          <MenuItem
            selected={value.preset === todayBalanceSheetPeriodMenuItem.preset}
            onClick={() => handlePresetSelect(todayBalanceSheetPeriodMenuItem.preset)}
          >
            <ListItemText>{todayBalanceSheetPeriodMenuItem.label}</ListItemText>
            <Typography color="textSecondary">
              {getBalanceSheetPeriodMenuDescription(todayBalanceSheetPeriodMenuItem.preset)}
            </Typography>
          </MenuItem>
        )}
        {showTodayOption && <Divider />}
        {currentBalanceSheetPeriodMenuItems.map((item) => (
          <MenuItem
            key={item.preset}
            selected={value.preset === item.preset}
            onClick={() => handlePresetSelect(item.preset)}
          >
            <ListItemText>{item.label}</ListItemText>
            <Typography color="textSecondary">{getBalanceSheetPeriodMenuDescription(item.preset)}</Typography>
          </MenuItem>
        ))}

        <Divider />

        {lastBalanceSheetPeriodMenuItems.map((item) => (
          <MenuItem
            key={item.preset}
            selected={value.preset === item.preset}
            onClick={() => handlePresetSelect(item.preset)}
          >
            <ListItemText>{item.label}</ListItemText>
            <Typography color="textSecondary">{getBalanceSheetPeriodMenuDescription(item.preset)}</Typography>
          </MenuItem>
        ))}

        <Divider />

        <MenuItem
          selected={value.preset === customBalanceSheetPeriodMenuItem.preset}
          onClick={() => {
            setMenuAnchorEl(null);
            setCustomRangeAnchorEl(triggerEl);
            setShowCustomRange(true);
          }}
        >
          <ListItemText>{customBalanceSheetPeriodMenuItem.label}</ListItemText>
        </MenuItem>
      </Menu>
      <Popover
        open={showCustomRange}
        onClose={() => setShowCustomRange(false)}
        anchorEl={customRangeAnchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box onMouseDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
          <DateRangeSelector
            from={range.from}
            to={range.to}
            onCancel={() => setShowCustomRange(false)}
            onApply={(customRange) => {
              onChange(createCustomBalanceSheetPeriodState({ from: customRange[0], to: customRange[1] }));
              setShowCustomRange(false);
            }}
          />
        </Box>
      </Popover>
    </>
  );
};

export default BalanceSheetPeriodPicker;
